import React, { useEffect, useState } from "react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Modal } from "@/components/ui/Modal";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { PanelList } from "@/components/ui/PanelList";
import { PanelListItem } from "@/components/ui/PanelListItem";
import {
  ArrowUpIcon,
  CheckIcon,
  CloseIcon,
  DocumentIcon,
  EditIcon,
  PlusIcon,
  SpinnerIcon,
  TrashIcon,
} from "@/components/icons";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import {
  useObjectiveTypes,
  useCreateObjectiveType,
  useUpdateObjectiveType,
  useArchiveObjectiveType,
  ObjectiveType,
} from "./hooks/useObjectiveTypes";
import { swapPair } from "./objectiveTypeOrder";

interface NewObjectiveTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (label: string) => Promise<void>;
}

/** Centered create modal (DD-006) replacing the old inline input. */
function NewObjectiveTypeModal({ isOpen, onClose, onCreate }: NewObjectiveTypeModalProps) {
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLabel("");
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) return;
    setIsSaving(true);
    setError(null);
    try {
      await onCreate(trimmed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create objective type");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isSaving) onClose();
      }}
      title="New objective type"
      size="sm"
      bodyPadding
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="objective-type-label-input" className="text-sm font-medium text-gray-900">
            Nome
          </label>
          <input
            id="objective-type-label-input"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="New objective type (e.g. educational)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors text-sm"
            disabled={isSaving}
            autoFocus
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={isSaving || !label.trim()}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? <SpinnerIcon className="w-4 h-4" /> : <PlusIcon className="w-3.5 h-3.5" />}
            Add
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Agency-level managed list of idea objective types ("commercial",
 * "awareness", "informative", ...), as a Setup panel (DD-047). Create opens
 * a modal from the header; rename, reorder and archive stay on the rows.
 * Backed by objectiveTypes.service via react-query. If the backend endpoint
 * is absent, the list degrades to the empty state and create stays usable.
 */
export function ObjectiveTypesCard() {
  const [collapsed, setCollapsed] = useState(false);
  const { data, isLoading } = useObjectiveTypes();
  const createMutation = useCreateObjectiveType();
  const updateMutation = useUpdateObjectiveType();
  const archiveMutation = useArchiveObjectiveType();

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [toArchive, setToArchive] = useState<ObjectiveType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const items = (data ?? [])
    .filter((t) => !t.isArchived)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const handleCreate = async (label: string) => {
    const nextSortOrder = items.length
      ? Math.max(...items.map((t) => t.sortOrder)) + 1
      : 0;
    await createMutation.mutateAsync({ label, sortOrder: nextSortOrder });
  };

  const startRename = (item: ObjectiveType) => {
    setEditingId(item.id);
    setEditingLabel(item.label);
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingLabel("");
  };

  const saveRename = async (item: ObjectiveType) => {
    const label = editingLabel.trim();
    if (!label || label === item.label) {
      cancelRename();
      return;
    }
    setError(null);
    try {
      await updateMutation.mutateAsync({ id: item.id, input: { label } });
      cancelRename();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rename objective type");
    }
  };

  const move = async (item: ObjectiveType, direction: -1 | 1) => {
    const pair = swapPair(items, item.id, direction);
    if (!pair) return;
    const [a, b] = pair;
    setError(null);
    try {
      await Promise.all([
        updateMutation.mutateAsync({ id: a.id, input: { sortOrder: b.sortOrder } }),
        updateMutation.mutateAsync({ id: b.id, input: { sortOrder: a.sortOrder } }),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reorder");
    }
  };

  const handleArchive = async () => {
    if (!toArchive) return;
    const target = toArchive;
    setToArchive(null);
    setError(null);
    try {
      await archiveMutation.mutateAsync(target.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to archive objective type");
    }
  };

  return (
    <>
      <WorkspacePanel
        title="Objective Types"
        actions={
          <PanelHeaderAction
            label="New objective type"
            icon={<PlusIcon className="w-4 h-4" />}
            onClick={() => setShowCreate(true)}
          />
        }
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        className={collapsed ? "" : "h-full"}
        loading={isLoading}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Shared strategic vocabulary chosen when an idea is created (e.g.
            commercial, awareness, informative). Add, rename, reorder, or archive
            entries. Used across all clients.
          </p>

          {error && (
            <div className="px-3 py-2 rounded-lg text-sm bg-red-50 text-red-800">
              {error}
            </div>
          )}

          <PanelList
            items={items}
            keyFor={(item) => item.id}
            empty={
              <div className="text-center py-6">
                <DocumentIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-900">No objective types yet.</p>
              </div>
            }
            renderItem={(item) => (
              <PanelListItem
                leading={
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => move(item, -1)}
                      disabled={swapPair(items, item.id, -1) === null || updateMutation.isPending}
                      className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                      aria-label="Move up"
                    >
                      <ArrowUpIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(item, 1)}
                      disabled={swapPair(items, item.id, 1) === null || updateMutation.isPending}
                      className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                      aria-label="Move down"
                    >
                      <ArrowUpIcon className="w-3.5 h-3.5 rotate-180" />
                    </button>
                  </div>
                }
                title={
                  editingId === item.id ? (
                    <input
                      type="text"
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename(item);
                        if (e.key === "Escape") cancelRename();
                      }}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-inset focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors text-sm font-normal"
                      autoFocus
                    />
                  ) : (
                    item.label
                  )
                }
                meta={
                  editingId !== item.id && item.key ? (
                    <span className="text-xs text-gray-400 font-mono">{item.key}</span>
                  ) : undefined
                }
                trailing={
                  editingId === item.id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => saveRename(item)}
                        disabled={updateMutation.isPending}
                        className="p-1.5 text-green-600 hover:text-green-700"
                        title="Save"
                        aria-label="Save"
                      >
                        <CheckIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelRename}
                        className="p-1.5 text-gray-400 hover:text-gray-700"
                        title="Cancel"
                        aria-label="Cancel"
                      >
                        <CloseIcon className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => startRename(item)}
                        className="p-1.5 text-gray-400 hover:text-gray-700"
                        title="Rename"
                        aria-label="Rename"
                      >
                        <EditIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setToArchive(item)}
                        className="p-1.5 text-gray-400 hover:text-red-500"
                        title="Archive"
                        aria-label="Archive"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </>
                  )
                }
              />
            )}
          />
        </div>
      </WorkspacePanel>

      <NewObjectiveTypeModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
      />

      <ConfirmDialog
        isOpen={toArchive !== null}
        onClose={() => setToArchive(null)}
        onConfirm={handleArchive}
        title="Archive Objective Type"
        message={`Archive "${toArchive?.label ?? ""}"? It will no longer be selectable for new ideas. Existing ideas keep their value.`}
        confirmText="Archive"
        variant="warning"
        isLoading={archiveMutation.isPending}
      />
    </>
  );
}
